<template>
  <b-row class="jobs-filter-where">

    <b-col v-bind="cols">
      <b-form-group :label="$t('jobs.labels.title')" label-for="title">
        <b-form-input id="title" placeholder="e.g. Designer, Manager" v-model.trim="form.title"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('jobs.labels.description')" label-for="description">
        <b-form-input id="description" placeholder="Keywords in description" v-model.trim="form.description"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols">
      <b-form-group :label="$t('jobs.labels.location')" label-for="location" v-if="useLocationEnum">
        <b-form-select id="location" :disabled="filterCustomField('location').length === 0" v-model="form.location">
          <option :value="null">{{ $t('ctas.select-option') }}</option>
          <option v-for="(value, key) in filterCustomField('location')" :key="key" :value="value">{{ value }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group :label="$t('jobs.labels.location')" label-for="location" v-else>
        <location-search id="location" v-model="form.location"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols" v-if="isInternalMobilityEnabled">
      <job-skills-selector name="Skills" :settings="{ noHeader: true }" v-model="form.skills" />
    </b-col>

    <b-col v-bind="cols" v-if="!hideDepartmentSelector && !isInternalMobilityEnabled">
      <template v-if="isCommunitySite">
        <b-form-group :label="$t('jobs.labels.companyName')" label-for="companyName">
          <b-form-input id="companyName" placeholder="e.g. Google, Facebook" v-model.trim="form.companyName"/>
        </b-form-group>
      </template>
      <template v-else-if="company.handle === 'bupadental'">
        <b-form-group label="Job Category" label-for="jobCategory">
          <b-form-select id="jobCategory" :disabled="filterCustomField('jobCategory').length === 0" v-model="form.jobCategory">
            <option :value="null">{{ $t('ctas.select-option') }}</option>
            <option v-for="(value, key) in filterCustomField('jobCategory')" :key="key" :value="value">{{ value }}</option>
          </b-form-select>
        </b-form-group>
      </template>
      <b-form-group v-else-if="useDepartmentsEnum" :label="$t('jobs.labels.department')" label-for="department">
        <b-form-select id="department" :disabled="filterCustomField('department').length === 0" v-model="form.department">
          <option :value="null">{{ $t('ctas.select-option') }}</option>
          <option v-for="(value, key) in filterCustomField('department')" :key="key" :value="value">{{ value }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-else :label="$t('jobs.labels.department')" label-for="department">
        <b-form-select id="department" :disabled="!departments.length" v-model="form.department">
          <option :value="null">{{ $t('ctas.select-option') }}</option>
          <option v-for="({ id, name, icon }) in departments" :key="id" :value="id">
            <font-awesome-icon v-if="icon" :icon="icon"/>
            {{ name }}
          </option>
        </b-form-select>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols" v-if="canSyncContacts">
      <b-form-group :label="$t('jobs.labels.matches')" label-for="matches">
        <toggle id="matches" :label-true="$t('jobs.all-jobs')" :label-false="$t('jobs.matches-only')" v-model="form.ignoreMatches"/>
      </b-form-group>
    </b-col>

    <b-col v-bind="cols" v-if="isLoggedIn">
      <label for="toggle">{{ $t('jobs.labels.status') }}</label>
      <toggle :label-true="$t('jobs.status.open')" :label-false="$t('jobs.status.closed')"
        :value="form.status === 'ACTIVE'" @input="t => t ? form.status = 'ACTIVE' : form.status = 'INACTIVE'"/>
    </b-col>

  </b-row>
</template>

<script>
import Toggle from '@/components/misc/toggle';
import LocationSearch from '@/components/misc/LocationSearch';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import { mapRoot } from '@/mixins/root';

export default {
  name: 'JobsFilterWhere',
  props: {
    orientation: {
      type: String,
      default: () => 'horizontal',
      validate: layout => [ 'horizontal', 'vertical' ].includes(layout),
    },
    whereCustomFields: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    Toggle, LocationSearch, JobSkillsSelector
  },
  data() {
    const form = JSON.parse(JSON.stringify(this.value));

    if (!this.useDepartmentsEnum && form.department && form.department.id) {
      form.department = form.department.id;
    } else {
      form.department = null;
    }

    if (!this.useLocationEnum && form.locationWhere && form.locationWhere.id) {
      form.location = { id: form.locationWhere.id };
    } else {
      form.location = null;
    }

    if (form.matches) {
      form.ignoreMatches = false;
      delete form.matches;
    } else {
      form.ignoreMatches = true;
    }

    if (Array.isArray(form.customFields)) {
      const roleType = form.customFields.find(({ key }) => key === 'role-type');
      if (roleType && roleType.value) {
        form.roleType = roleType.value;
      } else {
        form.roleType = null;
      }

      const jobCategory = form.customFields.find(({ key }) => key === 'job-category');
      if (jobCategory && jobCategory.value) {
        form.jobCategory = jobCategory.value;
      } else {
        form.jobCategory = null;
      }

      const contractType = form.customFields.find(({ key }) => key === 'contract-type');
      if (contractType && contractType.value) {
        form.contractType = contractType.value;
      } else {
        form.contractType = null;
      }

      const companyName = form.customFields.find(({ key }) => key === 'companyName');
      if (companyName && companyName.value) {
        form.companyName = companyName.value;
      } else {
        form.companyName = null;
      }

      const department = form.customFields.find(({ key }) => key === 'department');
      if (this.useDepartmentsEnum && department && department.value) {
        form.department = department.value;
      } else {
        form.department = null;
      }

      const location = form.customFields.find(({ key }) => key === 'location');
      if (this.useLocationEnum && location && location.value) {
        form.location = location.value;
      } else {
        form.location = null;
      }
    } else {
      form.roleType = null;
      form.contractType = null;
      form.companyName = null;
    }
    delete form.customFields;

    if (!form.hasOwnProperty('status') || typeof form.status !== 'string') {
      form.status = 'ACTIVE';
    }

    form.skills = Array.isArray(form.skills) && form.skills.length ? form.skills : null;

    return { form };
  },
  computed: {
    ...mapRoot([ 'isInternalMobilityEnabled', 'canSyncContacts', 'company', 'isLoggedIn', 'isCommunitySite', 'isEnumLocation' ]),
    cols() {
      if (this.orientation === 'horizontal') {
        return { cols: 12, sm: 6, md: 4 };
      } else {
        return { cols: 12 };
      }
    },
    departments() {
      const { departments } = this.company || {};
      const options = Array.isArray(departments) ? departments : [];
      return options.sort((a, b) => a.name.localeCompare(b.name));
    },
    useDepartmentsEnum() {
      const { handle } = this.company || {};
      return [ 'ocadogroup', 'optiver-interns' ].includes(handle);
    },
    useLocationEnum() {
      const { handle } = this.company || {};
      return this.isEnumLocation || [ 'ashfield', 'ocadogroup', 'optiver-apac', 'gwr' ].includes(handle);
    },
    hideDepartmentSelector() {
      // const { handle } = this.company || {};
      // return [ 'optiver' ].includes(handle);
      return false;
    },
  },
  methods: {
    filterCustomField(key) {
      const { [key]: customField } = this.whereCustomFields || {};
      return Array.isArray(customField) ? customField : [];
    },
  },
  debounceWatch: {
    form: {
      handler(form) {
        const {
          tags, location, department, ignoreMatches,
          roleType, jobCategory, contractType, companyName,
          skills,
          ...where
        } = JSON.parse(JSON.stringify(form));

        if (this.useDepartmentsEnum) {
          if (department) {
            where.customFields = where.customFields || [];
            where.customFields.push({ key: 'department', value: department });
          }
        } else if (department) {
          where.department = { id: department };
        }

        if (this.useLocationEnum) {
          if (location) {
            where.customFields = where.customFields || [];
            where.customFields.push({ key: 'location', value: location });
          }
        } else {
          if (location && location.latlon) {
            where.locationWhere = { latlon: location.latlon, latlonWithin: '20mi' };
          } else if (location && location.id) {
            where.locationWhere = { id: location.id };
          } else {
            where.locationWhere = null;
          }
        }

        if (tags && tags.includes(',')) {
          where.tags_in = tags.split(',').map(tag => tag.trim()).filter(tag => tag && tag.length);
        }

        where.skills = Array.isArray(skills) && skills.length ? skills : null;

        if (ignoreMatches === false) {
          where.matches = { gte: 0 };
        }

        if (roleType) {
          where.customFields = where.customFields || [];
          where.customFields.push({ key: 'role-type', value: roleType });
        }
        if (jobCategory) {
          where.customFields = where.customFields || [];
          where.customFields.push({ key: 'job-category', value: jobCategory });
        }
        if (contractType) {
          where.customFields = where.customFields || [];
          where.customFields.push({ key: 'contract-type', value: contractType });
        }
        if (companyName) {
          where.customFields = where.customFields || [];
          where.customFields.push({ key: 'companyName', value: companyName });
        }

        this.$emit('input', where);
      },
      wait: '1s',
      deep: true,
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/jobs/FilterWhere.vue